import React, { useEffect, useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Box,
  TextField,
  Divider,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import api from "../../services/api";
import DoctorCalendar from "../DoctorCalendar";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const AssignAppointmentModal = ({ open, onClose, ticket }) => {
  const { user } = useContext(AuthContext);

  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [patientName, setPatientName] = useState("");

  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingTitles, setLoadingTitles] = useState(false);

  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [doctorTitles, setDoctorTitles] = useState([]);
  const [selectedTitleId, setSelectedTitleId] = useState(null);
  const [errorEspecialidad, setErrorEspecialidad] = useState(false);

  useEffect(() => {
    const resetState = () => {
      setDoctors([]);
      setSelectedDoctor("");
      setDoctorTitles([]);
      setSelectedTitleId(null);
      setErrorEspecialidad(false);
      setPatientName("");
      setPatientSuggestions([]);
    };

    if (open) {
      if (user.isDoctor) {
        setSelectedDoctor(user.id);
      } else {
        setLoadingDoctors(true);
        api
          .get("/users", { params: { searchParam: "", pageNumber: 1 } })
          .then(({ data }) => {
            const onlyDoctors = data.users.filter((u) => u.isDoctor);
            setDoctors(onlyDoctors);
          })
          .finally(() => setLoadingDoctors(false));
      }

      if (ticket?.contact?.id) {
          setLoadingPatients(true);
          api.get("/contacts/" + ticket.contact.id + "/patients")
             .then(({ data }) => {
                setPatientSuggestions(data);
                setPatientName(ticket.title || ticket.contact.name || "");
             })
             .catch(err => toastError(err))
             .finally(() => setLoadingPatients(false));
      }
    } else {
      resetState();
    }
  }, [open, user, ticket]);

  useEffect(() => {
    if (!selectedDoctor) {
      setDoctorTitles([]);
      setSelectedTitleId(null);
      return;
    }
    setLoadingTitles(true);
    api.get("/users/" + selectedDoctor)
      .then(({ data }) => {
        const titles = data.professionalTitles || [];
        setDoctorTitles(titles);

        if (titles.length === 1) {
          setSelectedTitleId(titles[0].id);
        } else {
          setSelectedTitleId(null);
        }
      })
      .catch(err => console.error("Error cargando especialidades del doctor", err))
      .finally(() => setLoadingTitles(false));
  }, [selectedDoctor]);

  useEffect(() => {
    if (doctorTitles.length > 1 && !selectedTitleId) {
      setErrorEspecialidad(true);
    } else {
      setErrorEspecialidad(false);
    }
  }, [selectedTitleId, doctorTitles]);

  const isLoading = loadingDoctors || loadingTitles || loadingPatients;
  const canShowCalendar = patientName && selectedDoctor && selectedTitleId;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Asignar Cita</DialogTitle>
      <DialogContent>
        {isLoading && !canShowCalendar ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom>
              Paso 1: Seleccione Paciente y Profesional
            </Typography>
            <Box display="flex" flexDirection="column" gap={2} mb={2}>
              <Autocomplete
                freeSolo
                options={patientSuggestions}
                value={patientName}
                onInputChange={(event, newInputValue) => {
                    setPatientName(newInputValue);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Seleccione o escriba el nombre del paciente."
                        variant="outlined"
                        required
                    />
                )}
              />

              <Box display="flex" gap={2}>
                {!user.isDoctor && (
                  <FormControl fullWidth>
                    <InputLabel>Seleccionar doctor</InputLabel>
                    <Select
                      value={selectedDoctor}
                      onChange={(e) => setSelectedDoctor(e.target.value)}
                    >
                      {doctors.map((doc) => (
                        <MenuItem key={doc.id} value={doc.id}>
                          {doc.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedDoctor && doctorTitles.length > 0 && (
                  <FormControl fullWidth error={errorEspecialidad}>
                    {doctorTitles.length === 1 ? (
                      <Typography
                        variant="body1"
                        style={{
                          padding: "18.5px 14px",
                          border: "1px solid rgba(0, 0, 0, 0.23)",
                          borderRadius: "4px",
                        }}
                      >
                        {doctorTitles[0].title}
                      </Typography>
                    ) : (
                      <>
                        <InputLabel>Seleccionar especialidad</InputLabel>
                        <Select
                          value={selectedTitleId || ""}
                          onChange={(e) => setSelectedTitleId(e.target.value)}
                        >
                          {doctorTitles.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                              {t.title}
                            </MenuItem>
                          ))}
                        </Select>
                        {errorEspecialidad && (
                          <FormHelperText>
                            Seleccione una especialidad para continuar
                          </FormHelperText>
                        )}
                      </>
                    )}
                  </FormControl>
                )}
              </Box>
            </Box>
            
            <Divider style={{ margin: "16px 0" }} />

            {canShowCalendar ? (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Paso 2: Seleccione un horario disponible
                </Typography>
                <div style={{ height: "550px", width: "100%" }}>
                  <DoctorCalendar
                    doctorId={selectedDoctor}
                    ticket={ticket}
                    isAssignMode={true}
                    selectedTitleId={selectedTitleId}
                    onClose={onClose}
                    patientNameToAssign={patientName} 
                  />
                </div>
              </>
            ) : (
                <Typography color="textSecondary" align="center">
                    Complete los campos anteriores para ver el calendario.
                </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignAppointmentModal;